import Image from 'next/image'
import { Box, Grid, Link, Paper, Typography } from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined'
import { useTranslations } from 'next-intl'

import * as COLORS from '@/app/theme/colors/flyin'
import type { TopHotel } from '@/src/types/destinationResult'

interface TopHotelsProps {
    hotels: TopHotel[]
}

const HotelCard: React.FC<{ hotel: TopHotel }> = ({ hotel }) => {
    return (
        <Link href={hotel.url} underline="none" target="_blank" rel="noopener noreferrer">
            <Paper
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    maxWidth: 180,
                    maxHeight: 200,
                    '&:hover': { boxShadow: 3, borderColor: 'primary.light' },
                    transition: 'box-shadow 0.2s, border-color 0.2s',
                    cursor: 'pointer'
                }}
            >
                <Box sx={{ position: 'relative', aspectRatio: '4 / 3' }}>
                    {hotel.image ? (
                        <Image
                            src={hotel.image}
                            alt={hotel.name_en}
                            fill
                            style={{ objectFit: 'cover' }}
                            sizes="(max-width: 600px) 45vw, 160px"
                        />
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                bgcolor: COLORS.BRAND_PRIMARY_100,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <HotelOutlinedIcon sx={{ fontSize: 32, color: 'primary.light' }} />
                        </Box>
                    )}
                    {hotel.starRating && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 6,
                                right: 6,
                                bgcolor: COLORS.DESTINATION_HOTEL_CARD_OVERLAY,
                                borderRadius: 1,
                                px: 0.75,
                                py: 0.25,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.25
                            }}
                        >
                            <StarIcon sx={{ fontSize: 12, color: COLORS.BRAND_SECONDARY }} />
                            <Typography variant="caption" sx={{ color: 'common.white', fontSize: '0.65rem', fontWeight: 700 }}>
                                {hotel.starRating}
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Box sx={{ p: 1.25 }}>
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{
                            fontWeight: 600,
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.4,
                            color: 'text.primary'
                        }}
                    >
                        {hotel.name}
                    </Typography>
                </Box>
            </Paper>
        </Link>
    )
}

const TopHotels: React.FC<TopHotelsProps> = ({ hotels }) => {
    const t = useTranslations('destinationPage')
    const displayHotels = hotels.slice(0, 8)

    return (
        <Box component="section" sx={{ position: 'sticky', top: 24 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                <HotelOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography component="h2" variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.1rem' } }}>
                    {t('topHotels')}
                </Typography>
            </Box>

            <Grid container spacing={{ xs: 1, sm: 1.5 }} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {displayHotels.map(hotel => (
                    <Grid key={hotel.code} size={{ xs: 4, sm: 6 }} component="li">
                        <HotelCard hotel={hotel} />
                    </Grid>
                ))}
            </Grid>

            {hotels.length > 8 && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Link
                        href="#"
                        variant="body2"
                        sx={{ color: 'primary.main', fontWeight: 600 }}
                    >
                        {t('viewAllHotels')} →
                    </Link>
                </Box>
            )}
        </Box>
    )
}

export default TopHotels
export type { TopHotelsProps }
